import { useState } from "react";
import { Loader2, Check, Phone } from "lucide-react";
import { Reveal, MaskLine, SectionLabel } from "./Reveal";
import { LOCATIONS } from "../data/site";
import { trackEvent } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Enquiry = () => {
  const [form, setForm] = useState({
    full_name: "",
    phone: "",
    location: LOCATIONS[0].id,
  });
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");

  const shop = LOCATIONS.find((l) => l.id === form.location);

  const submit = async (e) => {
    e.preventDefault();
    setState("sending");
    setError("");
    try {
      const res = await fetch(`${API}/enquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Something went wrong.");
      trackEvent("enquiry_submit", `Enquiry · ${shop.label}`);
      setState("done");
    } catch (err) {
      setError(err.message);
      setState("idle");
    }
  };

  return (
    <section
      id="enquiry"
      data-testid="enquiry-section"
      className="relative border-t border-white/[0.06] px-6 py-24 sm:py-32 lg:px-12"
    >
      <div className="mx-auto max-w-3xl">
        <SectionLabel testId="enquiry-label">Chapter 07 · Not a caller?</SectionLabel>

        <h2 className="mt-7 font-cond text-4xl font-black uppercase leading-[0.9] tracking-tight text-[#f3ece0] sm:text-5xl">
          <MaskLine>Leave your number.</MaskLine>
          <MaskLine delay={0.08}>
            <span className="text-[#c5a059]">We'll call you back.</span>
          </MaskLine>
        </h2>

        <Reveal delay={0.12}>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-300">
            Three fields, ten seconds. Tell us which shop and a barber from that
            chair will ring you to lock in a time — no app, no account, no hold
            music.
          </p>
        </Reveal>

        {state === "done" ? (
          <Reveal>
            <div
              data-testid="enquiry-confirmed"
              className="mt-10 border border-[#c5a059]/30 bg-[#c5a059]/[0.06] p-8"
            >
              <Check size={26} className="text-[#c5a059]" />
              <p className="mt-4 font-cond text-2xl font-bold uppercase tracking-wide text-[#f3ece0]">
                Got it, {form.full_name.split(" ")[0]}.
              </p>
              <p className="mt-2 text-sm leading-relaxed text-slate-300">
                Our {shop.label} shop has your number and will reach out shortly.
                In a hurry?{" "}
                <a
                  href={`tel:${shop.tel}`}
                  data-testid="enquiry-call-now"
                  className="border-b border-[#c5a059]/50 text-[#e2cf9e]"
                >
                  Call {shop.phone}
                </a>{" "}
                and we'll pick up.
              </p>
            </div>
          </Reveal>
        ) : (
          <Reveal delay={0.18}>
            <form onSubmit={submit} className="mt-10" data-testid="enquiry-form">
              <div className="grid gap-3 sm:grid-cols-2">
                <input
                  required
                  value={form.full_name}
                  onChange={(e) =>
                    setForm({ ...form, full_name: e.target.value })
                  }
                  placeholder="Your name"
                  data-testid="enquiry-name-input"
                  className="h-14 w-full border border-white/10 bg-white/[0.03] px-4 text-sm text-slate-100 outline-none placeholder:text-slate-500 focus:border-[#c5a059]"
                />
                <input
                  required
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="Mobile number"
                  data-testid="enquiry-phone-input"
                  className="h-14 w-full border border-white/10 bg-white/[0.03] px-4 text-sm text-slate-100 outline-none placeholder:text-slate-500 focus:border-[#c5a059]"
                />
              </div>

              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                {LOCATIONS.map((l) => (
                  <button
                    key={l.id}
                    type="button"
                    aria-pressed={form.location === l.id}
                    onClick={() => setForm({ ...form, location: l.id })}
                    data-testid={`enquiry-shop-${l.id}`}
                    className={`border p-4 text-left transition-colors ${
                      form.location === l.id
                        ? "border-[#c5a059] bg-[#c5a059]/[0.08]"
                        : "border-white/[0.08] bg-white/[0.02] hover:border-[#c5a059]/45"
                    }`}
                  >
                    <span className="block font-cond text-base font-bold uppercase tracking-wide text-[#f3ece0]">
                      {l.label}
                    </span>
                    <span className="mt-1 block text-[11px] uppercase tracking-[0.12em] text-slate-400">
                      {l.address}
                    </span>
                  </button>
                ))}
              </div>

              {error && (
                <p
                  data-testid="enquiry-error"
                  className="mt-4 border border-red-400/30 bg-red-400/[0.06] p-3 text-xs text-red-300"
                >
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={state === "sending"}
                data-testid="enquiry-submit"
                className="mt-5 flex min-h-[56px] w-full items-center justify-center gap-2 bg-[#c5a059] px-8 font-cond text-base font-bold uppercase tracking-[0.14em] text-[#0a0d12] transition-colors hover:bg-[#d8b46b] disabled:opacity-60 sm:w-auto"
              >
                {state === "sending" && (
                  <Loader2 size={15} className="animate-spin" />
                )}
                Have the shop call me
              </button>

              <p className="mt-4 flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] text-slate-500">
                <Phone size={11} /> Or ring {shop.label} direct ·{" "}
                <a
                  href={`tel:${shop.tel}`}
                  data-testid="enquiry-direct-call"
                  className="text-[#c5a059]"
                >
                  {shop.phone}
                </a>
              </p>
            </form>
          </Reveal>
        )}
      </div>
    </section>
  );
};
